import { Icon } from '@iconify/react';
import { Button } from '@/components/shadcn/button';
import { Card, CardDescription, CardTitle } from '@/components/shadcn/card';
import type {
  BadmintonPlayerFormat,
  BadmintonScoringType,
  TambahGameFormState,
} from './badmintonGenerateUtils';
import BadmintonCompactStepper from './BadmintonCompactStepper';

interface TambahGameCoreFieldsProps {
  form: TambahGameFormState;
  onChange: (patch: Partial<TambahGameFormState>) => void;
}

const SCORING_OPTIONS: {
  value: BadmintonScoringType;
  number: string;
  title: string;
  hint: string;
}[] = [
  { value: 'rally_21', number: '21', title: 'Rally 21', hint: 'Reli poin 21, selisih 2, maksimal 30' },
  { value: 'rally_15', number: '15', title: 'Rally 15', hint: 'Reli poin 15, selisih 2, maksimal 20' },
  { value: 'rally_11', number: '11', title: 'Rally 11', hint: 'Reli poin 11, selisih 2, maksimal 15' },
];

const cardClass = 'rounded-2xl border-0 bg-white p-4 shadow-[inset_0_0_0_1px_#E0E6ED]';

export default function TambahGameCoreFields({ form, onChange }: TambahGameCoreFieldsProps) {
  return (
    <div className="space-y-3">
      <Card className={cardClass}>
        <CardTitle className="text-sm font-extrabold text-[#0E1726]">Kategori Main</CardTitle>
        <CardDescription className="mt-1 text-xs text-gray-400">
          Menentukan jumlah pemain per sisi lapangan.
        </CardDescription>
        <div className="mt-3 flex gap-2">
          {(['singles', 'doubles'] as BadmintonPlayerFormat[]).map((value) => {
            const selected = form.playerFormat === value;
            return (
              <Button
                key={value}
                type="button"
                variant="outline"
                aria-pressed={selected}
                onClick={() => onChange({ playerFormat: value, teams: [] })}
                className={`h-auto flex-1 rounded-full px-3 py-2 text-sm font-semibold ${
                  selected
                    ? 'border-[#FC480D] bg-[#FFF1EC] text-[#FC480D]'
                    : 'border-gray-200 bg-white text-gray-500'
                }`}
              >
                <Icon icon="mdi:account-group-outline" className="h-4 w-4" />
                {value === 'singles' ? 'Tunggal' : 'Ganda'}
              </Button>
            );
          })}
        </div>
      </Card>

      <Card className={cardClass}>
        <CardTitle className="text-sm font-extrabold text-[#0E1726]">Tipe Scoring</CardTitle>
        <CardDescription className="mt-1 text-xs text-gray-400">
          Batas poin tiap set yang dimainkan.
        </CardDescription>
        <div className="mt-3 space-y-2">
          {SCORING_OPTIONS.map((opt) => {
            const selected = form.scoringType === opt.value;
            return (
              <Button
                key={opt.value}
                type="button"
                variant="outline"
                aria-pressed={selected}
                onClick={() => onChange({ scoringType: opt.value })}
                className={`h-auto w-full items-center justify-start gap-3 rounded-2xl px-3 py-3 text-left ${
                  selected ? 'border-[#FC480D] bg-[#FFF8F5]' : 'border-gray-200 bg-white'
                }`}
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-xl text-sm font-extrabold ${
                    selected ? 'bg-[#FC480D] text-white' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {opt.number}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-extrabold text-[#0E1726]">{opt.title}</span>
                  <span className="block text-xs font-normal text-gray-400">{opt.hint}</span>
                </span>
                {selected ? (
                  <Icon icon="mdi:check-circle" className="h-5 w-5 text-[#FC480D]" />
                ) : (
                  <span className="h-5 w-5 rounded-full border border-gray-200" />
                )}
              </Button>
            );
          })}
        </div>
      </Card>

      <Card className={cardClass}>
        <CardTitle className="text-sm font-extrabold text-[#0E1726]">Jumlah Set per Game</CardTitle>
        <CardDescription className="mt-1 text-xs text-gray-400">
          Pemenang ditentukan dari mayoritas set.
        </CardDescription>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {(
            [
              { value: 1 as const, hint: 'Langsung selesai' },
              { value: 3 as const, hint: 'Best of three' },
              { value: 5 as const, hint: 'Best of five' },
            ]
          ).map((opt) => {
            const selected = form.maxSets === opt.value;
            return (
              <Button
                key={opt.value}
                type="button"
                variant="outline"
                aria-pressed={selected}
                onClick={() => onChange({ maxSets: opt.value })}
                className={`h-auto flex-col rounded-2xl px-2 py-3 ${
                  selected ? 'border-[#FC480D] bg-[#FFF8F5]' : 'border-gray-200 bg-white'
                }`}
              >
                <span className="block text-sm font-extrabold text-[#0E1726]">{opt.value} set</span>
                <span className="block text-[11px] font-normal text-gray-400">{opt.hint}</span>
              </Button>
            );
          })}
        </div>
      </Card>

      <Card className={`flex flex-row items-center justify-between gap-3 ${cardClass}`}>
        <div>
          <CardTitle className="text-sm font-extrabold text-[#0E1726]">Jumlah Lapangan</CardTitle>
          <CardDescription className="mt-1 text-xs text-gray-400">
            Lapangan yang dipakai paralel malam ini.
          </CardDescription>
        </div>
        <BadmintonCompactStepper
          label="Jumlah lapangan"
          value={form.numCourts}
          min={1}
          max={12}
          onChange={(numCourts) => onChange({ numCourts })}
        />
      </Card>
    </div>
  );
}
